import { useState, useEffect, useCallback, useMemo } from 'react';
import { getUpstashSensorDataCache } from '../utils/upstashRedisCache';
import { supabase } from '../services/supabaseClient';
import { AggregatedDataService } from '../services/AggregatedDataService';

/**
 * Hook for managing sensor readings with Redis
 * Uses AggregatedDataService (same as SensorPushWeb) for consistent data fetching
 */
export const useRedisReadings = (sensorId, timeRange = '24h') => {
  const [readings, setReadings] = useState([]);
  const [downsampledReadings, setDownsampledReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);

  const cache = getUpstashSensorDataCache();

  // Convert time range to milliseconds
  const getTimeRangeMs = useCallback((range) => {
    switch (range) {
      case '1h': return 1 * 60 * 60 * 1000;
      case '6h': return 6 * 60 * 60 * 1000;
      case '24h': return 24 * 60 * 60 * 1000;
      case '7d': return 7 * 24 * 60 * 60 * 1000;
      case '30d': return 30 * 24 * 60 * 60 * 1000;
      default: return 24 * 60 * 60 * 1000;
    }
  }, []);

  // Get period for caching based on time range
  const getCachePeriod = useCallback((range) => {
    switch (range) {
      case '1h': return 'HOUR';
      case '6h': return 'HOUR';
      case '24h': return 'DAY';
      case '7d': return 'WEEK';
      case '30d': return 'MONTH';
      default: return 'DAY';
    }
  }, []);

  // Downsample readings for chart performance
  const downsampleReadings = useCallback((readings, maxPoints = 500) => {
    if (readings.length <= maxPoints) return readings;

    const step = Math.ceil(readings.length / maxPoints);
    const downsampled = [];

    for (let i = 0; i < readings.length; i += step) {
      const chunk = readings.slice(i, i + step);
      if (chunk.length > 0) {
        const lastReading = chunk[chunk.length - 1];
        // Use recorded_at for timestamp (same as SensorPushWeb)
        const timestamp = lastReading.timestamp instanceof Date
          ? lastReading.timestamp
          : new Date(lastReading.recorded_at || lastReading.created_at);

        // Average the values in this chunk
        const avgReading = {
          ...chunk[0],
          temperature: chunk.reduce((sum, r) => sum + (r.temperature || 0), 0) / chunk.length,
          humidity: chunk.reduce((sum, r) => sum + (r.humidity || 0), 0) / chunk.length,
          battery_level: lastReading?.battery_level, // Use latest battery
          recorded_at: timestamp.toISOString(), // Use recorded_at (same as SensorPushWeb)
          created_at: timestamp.toISOString(),
          timestamp: timestamp // Keep Date object for chart plotting
        };
        downsampled.push(avgReading);
      }
    }

    return downsampled;
  }, []);

  // Load readings from Redis cache
  const loadFromCache = useCallback(async () => {
    if (!sensorId) return { readings: [], downsampled: [] };

    try {
      const timeMs = getTimeRangeMs(timeRange);
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - timeMs).toISOString();
      const period = getCachePeriod(timeRange);

      const cachedReadings = await cache.getCachedSensorReadings(sensorId, period, startTime, endTime);

      if (cachedReadings && cachedReadings.length > 0) {
        // Transform cached readings to match expected format (same as SensorPushWeb)
        const formattedReadings = cachedReadings.map(reading => {
          const timestamp = reading.timestamp instanceof Date
            ? reading.timestamp
            : new Date(reading.recorded_at || reading.created_at || reading.timestamp);

          return {
            ...reading,
            recorded_at: timestamp.toISOString(),
            created_at: timestamp.toISOString(),
            timestamp: timestamp
          };
        });

        const downsampled = downsampleReadings(formattedReadings);
        return { readings: formattedReadings, downsampled };
      }

      return { readings: [], downsampled: [] };
    } catch (error) {
      console.error('Error loading readings from cache:', error);
      return { readings: [], downsampled: [] };
    }
  }, [sensorId, timeRange, getTimeRangeMs, getCachePeriod, cache, downsampleReadings]);

  // Fetch readings using AggregatedDataService (same as SensorPushWeb)
  const fetchFromDatabase = useCallback(async () => {
    if (!sensorId) return;

    setIsSyncing(true);
    setError(null);

    try {
      const timeMs = getTimeRangeMs(timeRange);
      const endDate = new Date();
      const startDate = new Date(Date.now() - timeMs);
      const period = getCachePeriod(timeRange);

      console.log(`📊 Fetching aggregated data for period: ${period}, sensor: ${sensorId}`);
      console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);

      // Use AggregatedDataService for much faster and more accurate data (same as SensorPushWeb)
      const chartDataResponse = await AggregatedDataService.fetchChartData(
        sensorId,
        period,
        startDate,
        endDate
      );

      // Validate the response structure
      if (!chartDataResponse || !chartDataResponse.readings || !Array.isArray(chartDataResponse.readings)) {
        console.error('❌ Invalid chartDataResponse structure:', chartDataResponse);
        setReadings([]);
        setDownsampledReadings([]);
        return;
      }

      // Transform aggregated data to match existing format (same as SensorPushWeb DeviceDetailView)
      const formattedReadings = chartDataResponse.readings.map(reading => {
        // Ensure timestamp is a Date object (same as SensorPushWeb)
        const timestamp = reading.timestamp instanceof Date ? reading.timestamp : new Date(reading.timestamp);

        return {
          id: 0,
          sensor_id: sensorId,
          temperature: reading.temperature,
          humidity: reading.humidity,
          battery_level: reading.batteryLevel,
          // Key: use ISO string format for recorded_at and created_at (same as SensorPushWeb)
          recorded_at: timestamp.toISOString(),
          created_at: timestamp.toISOString(),
          timestamp: timestamp // Keep Date object for chart plotting
        };
      });

      console.log(`✅ Loaded ${formattedReadings.length} aggregated readings for ${period} period`);
      console.log(`📊 Total raw readings represented: ${chartDataResponse.totalReadings}`);

      // Downsample for chart display
      const downsampled = downsampleReadings(formattedReadings);

      setReadings(formattedReadings);
      setDownsampledReadings(downsampled);

    } catch (error) {
      console.error('❌ Error fetching readings:', error);
      setError(error.message);
    } finally {
      setIsSyncing(false);
    }
  }, [sensorId, timeRange, getTimeRangeMs, getCachePeriod, downsampleReadings]);

  // Initial load and real-time updates
  useEffect(() => {
    if (!sensorId) {
      setIsLoading(false);
      return;
    }

    let subscription = null;

    const initialize = async () => {
      setIsLoading(true);
      
      // Try to load from cache first
      const cachedData = await loadFromCache();
      setReadings(cachedData.readings);
      setDownsampledReadings(cachedData.downsampled);
      
      // Then fetch fresh data
      await fetchFromDatabase();
      
      // Setup real-time subscription for new readings
      subscription = supabase
        .channel(`sensor_readings_${sensorId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'sensor_readings',
            filter: `sensor_id=eq.${sensorId}`
          },
          async (payload) => {
            const newReading = payload.new;
            const timeMs = getTimeRangeMs(timeRange);
            // Use recorded_at for timestamp (same as SensorPushWeb)
            const readingTime = new Date(newReading.recorded_at || newReading.created_at).getTime();
            const cutoffTime = Date.now() - timeMs;

            // Only include if within time range
            if (readingTime >= cutoffTime) {
              // Format the new reading to match our data structure (same as SensorPushWeb)
              const timestamp = new Date(newReading.recorded_at || newReading.created_at);
              const formattedReading = {
                id: newReading.id,
                sensor_id: sensorId,
                temperature: newReading.temperature,
                humidity: newReading.humidity,
                battery_level: newReading.battery_level,
                recorded_at: timestamp.toISOString(),
                created_at: timestamp.toISOString(),
                timestamp: timestamp
              };

              setReadings(prev => {
                const updated = [...prev, formattedReading].sort((a, b) =>
                  new Date(a.recorded_at) - new Date(b.recorded_at)
                );
                const downsampled = downsampleReadings(updated);
                setDownsampledReadings(downsampled);
                return updated;
              });

              // Clear cache to force fresh data on next load
              const period = getCachePeriod(timeRange);
              await cache.clearSensorCache(sensorId);
            }
          }
        )
        .subscribe();
      
      setIsLoading(false);
    };

    initialize();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [sensorId, timeRange, loadFromCache, fetchFromDatabase, getTimeRangeMs, getCachePeriod, cache, downsampleReadings]);

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchFromDatabase();
  }, [fetchFromDatabase]);

  // Clear cache for this sensor
  const clearCache = useCallback(async () => {
    await cache.clearSensorCache(sensorId);
    await fetchFromDatabase();
  }, [cache, sensorId, fetchFromDatabase]);

  return {
    readings,
    downsampledReadings,
    isLoading,
    isSyncing,
    error,
    refresh,
    clearCache
  };
};
